import React, { useState, useMemo } from 'react';
import { Search, CheckCircle2, FileText, Building2 } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { formatINR } from '@/data/mockData';

const FILTERS = ['All', 'Pending', 'Under Review', 'Approved', 'Verified'];

const STATUS_FILTER_MAP = {
  'All':          null,
  'Pending':      ['PENDING_UPLOAD', 'OCR_CONFIRMATION'],
  'Under Review': ['UNDER_REVIEW'],
  'Approved':     ['APPROVED'],
  'Verified':     ['VERIFIED'],
};

const BADGE_STYLE = {
  PENDING_UPLOAD:   { label: 'Pending',      cls: 'bg-orange-50 text-orange-500' },
  OCR_CONFIRMATION: { label: 'Pending',      cls: 'bg-orange-50 text-orange-500' },
  UNDER_REVIEW:     { label: 'Under Review', cls: 'bg-blue-50 text-blue-600' },
  APPROVED:         { label: 'Approved',     cls: 'bg-green-50 text-green-600' },
  VERIFIED:         { label: 'Verified',     cls: 'bg-emerald-50 text-emerald-600' },
};

function StatusBadge({ status }) {
  const { label, cls } = BADGE_STYLE[status] || { label: status, cls: 'bg-gray-100 text-gray-500' };
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${cls}`}>
      {label}
    </span>
  );
}

export default function InvoiceSummaryScreen() {
  const { invoices, navigate } = useApp();
  const [search, setSearch]           = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  const totalSales    = invoices.reduce((s, i) => s + i.amount, 0);
  const approvedTotal = invoices
    .filter(i => ['APPROVED', 'VERIFIED'].includes(i.status))
    .reduce((s, i) => s + i.amount, 0);
  const pendingTotal  = invoices
    .filter(i => ['PENDING_UPLOAD', 'OCR_CONFIRMATION'].includes(i.status))
    .reduce((s, i) => s + i.amount, 0);

  const filtered = useMemo(() => {
    const statuses = STATUS_FILTER_MAP[activeFilter];
    let list = statuses ? invoices.filter(i => statuses.includes(i.status)) : invoices;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.invoiceNo.toLowerCase().includes(q) ||
        i.buyer.toLowerCase().includes(q)
      );
    }
    return list;
  }, [invoices, activeFilter, search]);

  const filteredTotal = filtered.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="min-h-screen bg-[#F5F6FA] pb-8">

      {/* Blue header */}
      <div className="bg-[#2563EB] px-4 pt-5 pb-5">
        <h1 className="text-white font-black text-2xl mb-4">Sales Invoices</h1>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/15 rounded-xl p-3">
            <div className="text-white/70 text-[9px] font-bold uppercase tracking-wider mb-1">Total Sales</div>
            <div className="text-white font-black text-sm">₹{formatINR(totalSales)}</div>
          </div>
          <div className="bg-white/15 rounded-xl p-3">
            <div className="text-white/70 text-[9px] font-bold uppercase tracking-wider mb-1">Approved</div>
            <div className="text-green-300 font-black text-sm">₹{formatINR(approvedTotal)}</div>
          </div>
          <div className="bg-white/15 rounded-xl p-3">
            <div className="text-white/70 text-[9px] font-bold uppercase tracking-wider mb-1">Pending</div>
            <div className="text-yellow-300 font-black text-sm">₹{formatINR(pendingTotal)}</div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">

        {/* Search */}
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="invoice-search"
            type="text"
            placeholder="Search invoice or hospital..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white rounded-2xl border border-gray-100 text-sm text-gray-700 placeholder-gray-400 outline-none shadow-sm"
          />
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4" style={{ scrollbarWidth: 'none' }}>
          {FILTERS.map(f => (
            <button
              key={f}
              data-testid={`filter-${f.replace(' ', '-').toLowerCase()}`}
              onClick={() => setActiveFilter(f)}
              className="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold tap-scale transition-colors"
              style={
                activeFilter === f
                  ? { background: '#2563EB', color: '#fff' }
                  : { background: '#fff', color: '#6B7280', border: '1px solid #E5E7EB' }
              }
            >
              {f}
            </button>
          ))}
        </div>

        {/* Count + total row */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-xs font-black text-gray-400 uppercase tracking-widest">
            {filtered.length} Invoice{filtered.length !== 1 ? 's' : ''}
          </span>
          <span className="text-sm font-black text-gray-700">₹{formatINR(filteredTotal)}</span>
        </div>

        {/* Invoice cards */}
        <div className="flex flex-col gap-3">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-300">
              <FileText size={44} color="#D1D5DB" className="mb-3" />
              <div className="font-semibold text-gray-400">No invoices found</div>
            </div>
          ) : (
            filtered.map(inv => {
              const isPositive = ['APPROVED', 'VERIFIED'].includes(inv.status);
              return (
                <div
                  key={inv.id}
                  data-testid={`invoice-card-${inv.id}`}
                  className="bg-white rounded-2xl p-4 shadow-sm"
                >
                  {/* Top: icon + invoice no/date + badge */}
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: isPositive ? '#F0FDF4' : '#EFF6FF' }}
                    >
                      {isPositive
                        ? <CheckCircle2 size={20} color="#22C55E" />
                        : <FileText size={20} color="#2563EB" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-gray-900 text-sm">
                        {['PENDING_UPLOAD', 'OCR_CONFIRMATION'].includes(inv.status) ? 'XXXXXXXX' : inv.invoiceNo}
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5">{inv.date}</div>
                    </div>
                    <StatusBadge status={inv.status} />
                  </div>

                  {/* Hospital + Sale Value */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-1 text-gray-400 mb-0.5" style={{ fontSize: '11px' }}>
                        <Building2 size={11} />
                        <span>Hospital</span>
                      </div>
                      <div className="font-bold text-gray-900 text-sm leading-tight">{inv.buyer}</div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <div className="text-gray-400 mb-0.5" style={{ fontSize: '11px' }}>Sale Value</div>
                      <div className="font-black text-gray-900 text-base">₹{formatINR(inv.amount)}</div>
                    </div>
                  </div>

                  {/* Products */}
                  {inv.products?.length > 0 && (
                    <div>
                      <div
                        className="text-gray-400 font-black uppercase tracking-widest mb-1.5"
                        style={{ fontSize: '10px' }}
                      >
                        Products ({inv.products.length})
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {inv.products.map(p => (
                          <span
                            key={p}
                            className="text-gray-600 font-medium px-2.5 py-0.5 rounded-full"
                            style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', fontSize: '11px' }}
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Small upload CTA for pending invoices */}
                  {['PENDING_UPLOAD', 'OCR_CONFIRMATION'].includes(inv.status) && (
                    <button
                      data-testid={`upload-btn-${inv.id}`}
                      onClick={() => navigate('UPLOAD_STATEMENT')}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-xl tap-scale"
                      style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span className="text-blue-600 font-bold text-xs">Upload Invoice</span>
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
